import { Component, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { VPillsComponent } from "@app/directives/_veri/v-pills/v-pills.component";
import { SortDirection } from "@app/enum/SortDirection";
import { GroupService } from "@app/services/groups/group.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { NavigationService } from "@services/navigation.service";
import { TranslationService } from "@services/translation.service";
import { IGroup } from "../groups/_models/IGroup";
import { ResourceManagementNavigationModule } from "../resourceManagement.navigation";
import './driving-styles-manager.component.scss';
import { IDrivingStyle } from "./driving-styles.interfaces";
import { DrivingStylesService } from "./driving-styles.service";
import { DrivingStyleType } from "./driving-styles.enums";

@Component({
    selector: 'driving-styles-manager',
    template: require('./driving-styles-manager.component.html')
})
export class DrivingStylesManagerComponent implements OnInit {

    groups: IGroup[];
    drivingStyles: IDrivingStyle[];

    filterTabs: IFilterBarTab<IDrivingStyle>[] = [];
    filterEvent: IAggregateFilterEvent<IDrivingStyle>;

    loading: boolean;
    showCreateSidebar: boolean;

    tableColumns: IVTableColumn<IDrivingStyle>[];
    tableRows: IVTableRow[];


    constructor(
        private readonly translationService: TranslationService,
        private readonly groupService: GroupService,
        private readonly navigationService: NavigationService,
        private readonly drivingStylesService: DrivingStylesService,
        private readonly vTableService: VTableService
    ) {
    }

    ngOnInit() {
        this.createTable();
        this.getData();
    }

    onTableRowClicked(tableRow: IVTableRow): void {
        if (!tableRow.data) {
            return;
        }

        this.editDrivingStyle(tableRow.data);
    }

    createNewDrivingStyle(): void {
        this.navigationService.go(ResourceManagementNavigationModule.NewDrivingStyle);
    }

    //--

    private async getData(): Promise<void> {

        if (this.loading) {
            return;
        }

        this.loading = true;

        const promises: Promise<any>[] = [
            (this.groupService.getGroups() as Promise<IGroup[]>),
            this.drivingStylesService.getDrivingStyles(true)
        ];

        await Promise.all(promises).then(([groups, drivingStyles]) => {
            this.groups = groups;
            this.drivingStyles = drivingStyles;

            this.addColumnDataForAssociations(this.drivingStyles);
        });

        this.createTabs();
        this.tableRows = this.vTableService.createTableRows(this.drivingStyles, this.tableColumns);

        this.loading = false;
    }

    private editDrivingStyle(drivingStyle: IDrivingStyle): void {
        if (!drivingStyle?.Id) {
            return;
        }

        this.navigationService.go(ResourceManagementNavigationModule.EditDrivingStyle, { drivingStyleId: drivingStyle.Id });
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition("Common.All", (drivingStyle) => true);
        this.createTabDefinition("DrivingStylesManager.Associated", (drivingStyle) => drivingStyle.DrivingStyleAssociations?.length > 0);
        this.createTabDefinition("DrivingStylesManager.Unassociated", (drivingStyle) => !drivingStyle.DrivingStyleAssociations?.length);
    }

    private createTabDefinition(translationString: string, callback: (drivingStyle: IDrivingStyle) => boolean): void {
        this.filterTabs.push({ title: this.translationService.t(translationString), predicate: callback });
    }

    private addColumnDataForAssociations(drivingStyles: IDrivingStyle[]): void {
        if (!drivingStyles?.length) {
            return;
        }

        drivingStyles.forEach(drivingStyle => {
            this.drivingStylesService.createAssociationPillsForDrivingStyle(drivingStyle, this.groups);
        });
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: 'DrivingStylesManager.TableColumnTitleName', width: 260 }),
                createCellFunction: style => this.vTableService.createTableCell(style.Name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'DrivingStylesManager.TableColumnTitleAppliesTo' }),
                createCellFunction: style => this.vTableService.createTableCell(this.getDataForAssociationsAsString(style), {
                    component: VPillsComponent,
                    componentInputs: {
                        pills: style.displayPillsForAssociations
                    }
                })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'DrivingStylesManager.TableColumnTitleType', width: 260 }),
                createCellFunction: style => this.vTableService.createEnumCell(DrivingStyleType, "DrivingStyleType", style.DrivingStyleType)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: 'DrivingStylesManager.TableColumnTitleLastUpdated', sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: style => this.vTableService.createDateCell(new Date(style.Updated))
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: 'DrivingStylesManager.TableColumnTitleCreated' }),
                createCellFunction: style => this.vTableService.createDateCell(new Date(style.Created))
            }
        ]
    }

    private getDataForAssociationsAsString(style: IDrivingStyle): string {
        let string = '';

        style.displayPillsForAssociations.forEach(pill => {
            string += ` ${pill.label}`;
        })

        return string;
    }
}
